import { LocalDate } from "@js-joda/core";
import assertNever from "assert-never";
import React from "react";
import { CommCenterTicketCreateParams } from "../../activities/AppNavigator";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import useCaregiver from "../../hooks/useCaregiver";
import useClockedInStatus from "../../hooks/useClockedInStatus";
import useMixin from "../../hooks/useMixin";
import usePatientsState from "../../hooks/usePatientsState";
import { useUploadVerificationSelfieMutation } from "../../hooks/useUploadVerificationSelfieMutation";
import { isLoading, Loading, loading } from "../../Loading";
import {
  ClockInOutCallType,
  ImportedVisitId,
  PatientId,
  PatientTaskInstanceId,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import {
  changeSchedule,
  decreaseScheduleByMonth,
  increaseScheduleByMonth,
} from "../../utils/handleScheduleDates";
import { List } from "../../utils/List";
import { CicoModalEntity, extractVisitFromCicoModalEntity } from "../../utils/visitUtils";
import { AssignedVisitsAndTasksList } from "../AssignedVisitsAndTasksList";
import ChangesSavedBanner from "../ChangesSavedBanner";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import LoadingScaffold from "../LoadingScaffold";
import MonthPicker from "../MonthPicker/MonthPicker";
import PatientNotExistScreen from "../Patient/PatientNotExistScreen";
import useContactSupport from "../useContactSupport";
import MedflytVisitLetsStartBottomSheet from "../VisitLetsStartBottomSheet/MedflytVisitLetsStartBottomSheet";
import VisitLetsStartBottomSheet from "../VisitLetsStartBottomSheet/VisitLetsStartBottomSheet";
import WithDutySheet from "../WithDutySheet";
import WithLetsStartBottomSheet from "../WithLetsStartBottomSheet";
import WithMedflytLetsStartBottomSheet from "../WithMedflytLetsStartBottomSheet";
import PatientVisitsAndTasksExtraHeader from "./components/PatientVisitsAndTasksExtraHeader";
import PatientVisitsAndTasksScreen from "./PatientVisitsAndTasksScreen";
import {
  PatientVisitsAndTasksDetailsState,
  PatientVisitsAndTasksScreenState,
} from "./PatientVisitsAndTasksTypes";

interface Props {
  api: API;
  appState: AppState;
  patientId: PatientId;
  listType: "Visits" | "Tasks";
  onRequestNavigate: (page: Page) => void;
  onClickNewChatWithMedflyt: (params: CommCenterTicketCreateParams) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onClickGoBack: () => void;
}

const PatientVisitsAndTasksActivity = (props: Props) => {
  const [screenState, setScreenState] = React.useState<PatientVisitsAndTasksScreenState>({
    status: "Loading",
  });
  const caregiver = useCaregiver(props.appState);
  const patientsState = usePatientsState(props.appState);
  const clockedInStatus = useClockedInStatus(props.appState);
  const mixin = useMixin(props.appState);
  const [scheduleYear, setScheduleYear] = React.useState(LocalDate.now().year());
  const [scheduleMonth, setScheduleMonth] = React.useState(LocalDate.now().month());
  const [isChangesSavedBannerOpen, setChangesSavedBannerOpen] = React.useState(false);
  const contactSupport = useContactSupport({
    api: props.api,
    appState: props.appState,
    onRequestNavigate: props.onRequestNavigate,
    onClickNewChatWithMedflyt: props.onClickNewChatWithMedflyt,
  });
  const [activeEntity, setActiveEntity] = React.useState<CicoModalEntity | Loading>(loading());
  const [isVisitBottomSheetOpen, setVisitBottomSheetOpen] = React.useState(false);

  const activeVisit = isLoading(activeEntity)
    ? activeEntity
    : extractVisitFromCicoModalEntity(activeEntity);

  React.useEffect(() => {
    if (
      !isLoading(patientsState) &&
      !isLoading(caregiver) &&
      !isLoading(clockedInStatus) &&
      !isLoading(mixin.scheduledVisits) &&
      !isLoading(mixin.appVisitEditRequestsState) &&
      !isLoading(mixin.appVisitsState) &&
      !isLoading(mixin.scheduledVisits.importedVisitDetails) &&
      !isLoading(mixin.appTasksState) &&
      !isLoading(contactSupport)
    ) {
      const patient = patientsState.patients.find((x) => x.id === props.patientId);

      if (patient === undefined) {
        setScreenState({ status: "NotExist" });
        return;
      }

      let details: PatientVisitsAndTasksDetailsState;
      if (props.listType === "Visits") {
        details = {
          listType: "Visits",
          clockedInStatus: clockedInStatus,
          visitInstances: mixin.scheduledVisits.scheduledVisitDetails.filter(
            (visit) => visit.publicVisitDetails.patientId === props.patientId
          ),
          rescheduleRequests: mixin.appVisitEditRequestsState.visitEditRequests,
          importedVisits: List.empty(),
        };
      } else {
        details = {
          listType: "Tasks",
          tasksInstances: mixin.appTasksState.tasks.filter(
            (taskInstance) => taskInstance.taskDetails.patientId === props.patientId
          ),
        };
      }

      setScreenState({
        status: "Ready",
        patient: patient,
        caregiver: caregiver,
        details: details,
        contactSupport: contactSupport,
      });
    }
  }, [
    caregiver,
    clockedInStatus,
    patientsState,
    mixin.appAgenciesState,
    mixin.appVisitEditRequestsState,
    mixin.appVisitsState,
    mixin.scheduledVisits,
    mixin.appTasksState,
    props.listType,
    props.patientId,
    contactSupport,
  ]);

  const handleClickPreviousScheduleMonth = React.useCallback(() => {
    return decreaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  }, [scheduleMonth, scheduleYear]);

  const handleClickNextScheduleMonth = React.useCallback(() => {
    return increaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  }, [scheduleMonth, scheduleYear]);

  const handleChangeScheduleMonth = React.useCallback(
    (newYear: number, newMonth: number) => {
      return changeSchedule(
        scheduleMonth,
        scheduleYear,
        newMonth,
        newYear,
        setScheduleMonth,
        setScheduleYear
      );
    },
    [scheduleMonth, scheduleYear]
  );

  const handleClickVisitInstance = React.useCallback(
    (visitInstanceId: VisitInstanceId) => {
      props.onRequestNavigate.call(null, {
        type: "VisitInstance",
        visitInstanceId: visitInstanceId,
      });
    },
    [props.onRequestNavigate]
  );

  const handleClickImportedVisit = React.useCallback(
    (importedVisitId: ImportedVisitId, patientName: string) => {
      props.onRequestNavigate.call(null, {
        type: "ConvertImportedVisit",
        importedVisitId: importedVisitId,
        patientName: patientName,
      });
    },
    [props.onRequestNavigate]
  );

  const handleClickTaskInstance = React.useCallback(
    (taskInstanceId: PatientTaskInstanceId, initialScheduleDateTimePickerOpen: boolean) => {
      props.onRequestNavigate.call(null, {
        type: "PatientTaskInstance",
        patientTaskInstanceId: taskInstanceId,
        initialScheduleDateTimePickerOpen: initialScheduleDateTimePickerOpen,
      });
    },
    [props.onRequestNavigate]
  );

  const uploadCerificationSelfieMutation = useUploadVerificationSelfieMutation({
    api: props.api,
  });

  const handleOpenChangesSavedBanner = () => {
    setChangesSavedBannerOpen(true);
  };

  const handleCloseChangesSavedBanner = () => {
    setChangesSavedBannerOpen(false);
  };

  const handleHideOrShowVisitBottomSheet = (isOpen: boolean) => {
    setVisitBottomSheetOpen(isOpen);
  };

  const handleOpenVisitBottomSheet = (entity: CicoModalEntity) => {
    setActiveEntity(entity);
    setVisitBottomSheetOpen(true);
  };

  const handleUpdateActiveVisit = (visitInstanceId: VisitInstanceId) => {
    if (isLoading(props.appState.scheduledVisits)) {
      return;
    }
    const visitInstance = props.appState.scheduledVisits.scheduledVisitDetails.find(
      (x) => x.id === visitInstanceId
    );
    if (visitInstance === undefined) {
      return;
    }
    handleOpenVisitBottomSheet({ type: "visit", visit: visitInstance });
  };

  const handleSubmitDutySheet = (visitInstanceId: VisitInstanceId) => {
    props.appState.setVisitInstanceDutySheetCompleted(visitInstanceId, true);
    if (!isLoading(activeEntity)) {
      switch (activeEntity.type) {
        case "task":
          setActiveEntity({
            type: "task",
            task: {
              ...activeEntity.task,
              visitInstance: {
                ...activeEntity.task.visitInstance,
                isDutySheetCompleted: true,
              },
            },
          });
          break;

        case "visit":
          setActiveEntity({
            type: "visit",
            visit: { ...activeEntity.visit, isDutySheetCompleted: true },
          });
          break;
      }
    }
  };

  const visitLetsStartBottomSheet = (() => {
    if (isLoading(activeEntity) || isLoading(activeVisit) || isLoading(caregiver)) {
      return null;
    }
    return (
      <WithDutySheet activeVisit={activeVisit} api={props.api}>
        {caregiver.isCaregiverOfMedFlyt
          ? (initialDutySheetData) => (
              <WithMedflytLetsStartBottomSheet
                activeEntity={activeEntity}
                api={props.api}
                appState={props.appState}
                initialDutySheetData={initialDutySheetData}
                isOpen={isVisitBottomSheetOpen}
                onHideOrShowVisitBottomSheet={handleHideOrShowVisitBottomSheet}
                onOpenChangesSavedBanner={handleOpenChangesSavedBanner}
                onUpdateActiveVisit={handleUpdateActiveVisit}
                onSubmitDutySheet={handleSubmitDutySheet}
              >
                {(bottomSheetProps) => (
                  <MedflytVisitLetsStartBottomSheet
                    {...bottomSheetProps}
                    contactSupport={contactSupport}
                  />
                )}
              </WithMedflytLetsStartBottomSheet>
            )
          : (initialDutySheetData) => (
              <WithLetsStartBottomSheet
                activeVisit={activeVisit}
                api={props.api}
                appState={props.appState}
                initialDutySheetData={initialDutySheetData}
                isOpen={isVisitBottomSheetOpen}
                onHideOrShowVisitBottomSheet={handleHideOrShowVisitBottomSheet}
                onOpenChangesSavedBanner={handleOpenChangesSavedBanner}
                onUpdateActiveVisit={handleUpdateActiveVisit}
                onSubmitDutySheet={handleSubmitDutySheet}
              >
                {(bottomSheetProps) => (
                  <VisitLetsStartBottomSheet
                    {...bottomSheetProps}
                    contactSupport={contactSupport}
                  />
                )}
              </WithLetsStartBottomSheet>
            )}
      </WithDutySheet>
    );
  })();

  const handleUploadVerificationSelfie = (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ): Promise<void> => {
    return uploadCerificationSelfieMutation.mutateAsync({
      visitInstanceId: visitInstanceId,
      imageBase64: imageBase64,
      clockType: clockType,
    });
  };

  switch (screenState.status) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "NotExist":
      return <PatientNotExistScreen onClickGoBack={props.onClickGoBack} />;
    case "Ready": {
      let title: string;
      let visitsOrTasksList: JSX.Element;
      switch (screenState.details.listType) {
        case "Visits":
          title = translate(STRINGS.LIST_OF_VISITS);
          visitsOrTasksList = (
            <>
              <AssignedVisitsAndTasksList
                caregiverId={screenState.caregiver.id}
                isCaregiverOfMedflyt={screenState.caregiver.isCaregiverOfMedFlyt}
                clockedInStatus={screenState.details.clockedInStatus}
                visitInstances={screenState.details.visitInstances}
                rescheduleRequests={screenState.details.rescheduleRequests}
                importedVisits={screenState.details.importedVisits}
                patientTasksInstances={List.empty()}
                scheduleView="DATE"
                scheduleViewMonth={scheduleMonth}
                scheduleViewYear={scheduleYear}
                contactSupportProps={screenState.contactSupport}
                onRequestNavigate={props.onRequestNavigate}
                onClock={props.onClock}
                onScheduledVisitClick={handleClickVisitInstance}
                onImportedVisitClick={handleClickImportedVisit}
                onClickTask={handleClickTaskInstance}
                onClickStartNow={handleOpenVisitBottomSheet}
                onRequestAvailableVisits={props.onClickGoBack}
                onUploadVerificationSelfie={handleUploadVerificationSelfie}
                onRequestVisitAdjustmentCheck={props.api.getCaregiverCanAdjustVisitInstanceTimes}
                onClickNextMonth={handleClickNextScheduleMonth}
                onCheckIfDocumentsSubmitted={props.api.checkIfAllDocumentsSubmitted}
                onCaregiverMarkVisitAsMissed={props.api.caregiverMarkVisitAsMissed}
              />
              {visitLetsStartBottomSheet}
            </>
          );
          break;
        case "Tasks":
          title = translate(STRINGS.LIST_OF_TASKS);
          visitsOrTasksList = (
            <AssignedVisitsAndTasksList
              caregiverId={screenState.caregiver.id}
              isCaregiverOfMedflyt={screenState.caregiver.isCaregiverOfMedFlyt}
              clockedInStatus={{ status: null }}
              visitInstances={List.empty()}
              rescheduleRequests={List.empty()}
              importedVisits={List.empty()}
              patientTasksInstances={screenState.details.tasksInstances}
              scheduleView="DATE"
              scheduleViewMonth={scheduleMonth}
              scheduleViewYear={scheduleYear}
              contactSupportProps={screenState.contactSupport}
              onRequestNavigate={props.onRequestNavigate}
              onClock={props.onClock}
              onScheduledVisitClick={handleClickVisitInstance}
              onImportedVisitClick={handleClickImportedVisit}
              onClickTask={handleClickTaskInstance}
              onClickStartNow={handleOpenVisitBottomSheet}
              onRequestAvailableVisits={props.onClickGoBack}
              onUploadVerificationSelfie={handleUploadVerificationSelfie}
              onRequestVisitAdjustmentCheck={props.api.getCaregiverCanAdjustVisitInstanceTimes}
              onClickNextMonth={handleClickNextScheduleMonth}
              onCheckIfDocumentsSubmitted={props.api.checkIfAllDocumentsSubmitted}
              onCaregiverMarkVisitAsMissed={props.api.caregiverMarkVisitAsMissed}
            />
          );
          break;
        default:
          assertNever(screenState.details);
      }

      const monthPicker = (
        <MonthPicker
          year={scheduleYear}
          month={scheduleMonth}
          onClickPrevious={handleClickPreviousScheduleMonth}
          onClickNext={handleClickNextScheduleMonth}
          onChange={handleChangeScheduleMonth}
        />
      );

      const extraHeader = (
        <AppSlideFadeIn delay={200}>
          <PatientVisitsAndTasksExtraHeader
            patient={screenState.patient}
            monthPicker={monthPicker}
          />
        </AppSlideFadeIn>
      );

      return (
        <>
          <PatientVisitsAndTasksScreen
            patient={screenState.patient}
            title={title}
            extraHeader={extraHeader}
            visitsAndTasksList={visitsOrTasksList}
            onClickGoBack={props.onClickGoBack}
          />
          <ChangesSavedBanner
            isOpen={isChangesSavedBannerOpen}
            onClose={handleCloseChangesSavedBanner}
          />
        </>
      );
    }
    default:
      assertNever(screenState);
  }
};

export default React.memo(PatientVisitsAndTasksActivity);
